import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Calendar, DollarSign, Package, FlaskConical, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { useClientPOs } from '@/hooks/useClientPOs';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const parseInvoiceDate = (dStr) => {
  if (!dStr) return 0;
  let d = new Date(dStr);
  if (isNaN(d.getTime()) && typeof dStr === 'string') {
    const str = dStr.toLowerCase();
    const months = { 'jan':0,'feb':1,'mar':2,'apr':3,'mei':4,'may':4,'jun':5,'jul':6,'agu':7,'aug':7,'sep':8,'okt':9,'oct':9,'nov':10,'des':11,'dec':11 };
    const parts = str.split(' ');
    if (parts.length >= 3) {
       const day = parseInt(parts[0]);
       const monthStr = Object.keys(months).find(m => parts[1].includes(m));
       const month = monthStr ? months[monthStr] : 0;
       const year = parseInt(parts[2]);
       d = new Date(year, month, day);
    }
  }
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

function isInRange(dateStr, start, end) {
  if (!dateStr) return false;
  try {
    const timestamp = parseInvoiceDate(dateStr);
    if (timestamp === 0) return false;
    const d = startOfDay(new Date(timestamp));
    const s = startOfDay(new Date(start));
    const e = startOfDay(new Date(end));
    return (isAfter(d, s) || isEqual(d, s)) && (isBefore(d, e) || isEqual(d, e));
  } catch { return false; }
}
const PRESETS = [
  { label: 'Hari ini', getValue: () => { const t = format(new Date(), 'yyyy-MM-dd'); return [t, t]; } },
  { label: '7 Hari', getValue: () => { const now = new Date(); return [format(new Date(now - 6 * 86400000), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: '30 Hari', getValue: () => { const now = new Date(); return [format(new Date(now - 29 * 86400000), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: 'Bulan Ini', getValue: () => { const now = new Date(); return [format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: 'Semua', getValue: () => ['2000-01-01', '2099-12-31'] },
];

export default function MarginKeseluruhan() {
  const [activePreset, setActivePreset] = useState('Semua');
  const [dateStart, setDateStart] = useState('2000-01-01');
  const [dateEnd, setDateEnd] = useState('2099-12-31');
  const [isCustom, setIsCustom] = useState(false);

  const { clientPOs = [], isLoading } = useClientPOs();

  const handlePreset = (preset) => {
    const [s, e] = preset.getValue();
    setDateStart(s);
    setDateEnd(e);
    setActivePreset(preset.label);
    setIsCustom(false);
  };

  const summary = useMemo(() => {
    let marginProduksi = 0;
    let marginJasa = 0;
    let marginLegalitas = 0;
    let totalTransaksi = 0;

    let breakdownMap = {
      PO: { id: 'PO', label: 'Margin PO', produksi: 0, jasa: 0, legalitas: 0, total: 0 },
      Sample: { id: 'Sample', label: 'Margin Sample', produksi: 0, jasa: 0, legalitas: 0, total: 0 },
      Custom: { id: 'Custom', label: 'Margin Custom', produksi: 0, jasa: 0, legalitas: 0, total: 0 }
    };

    clientPOs.forEach(po => {
      const isSelesai = po.statusProduksi === 'PESANAN SELESAI' || po.statusProduksi === 'SELESAI';
      const d = (isSelesai && (po.updated_at || po.rawRecord?.updated_at)) ? (po.updated_at || po.rawRecord.updated_at).split('T')[0] : (po.date || po.created_at?.split('T')[0] || '');
      if (!isInRange(d, dateStart, dateEnd)) return;

      let isCustom = po.is_custom_finance || po.poMode === "custom_finance";
      let isSample = po.isSample || po.poMode === "sample";
      let isPO = !isCustom && !isSample;
      
      let rawProducts = po.products || po.items || [];
      if (!Array.isArray(rawProducts)) {
        try { rawProducts = JSON.parse(rawProducts); } catch(e) { rawProducts = []; }
        if (!Array.isArray(rawProducts)) rawProducts = [];
      }

      let rawLegalitas = po.legalitas || [];
      if (!Array.isArray(rawLegalitas)) {
        try { rawLegalitas = JSON.parse(rawLegalitas); } catch(e) { rawLegalitas = []; }
        if (!Array.isArray(rawLegalitas)) rawLegalitas = [];
      }

      let poProdJual = 0;
      let poProdHpp = 0;
      let poJasa = 0;
      
      rawProducts.forEach(p => {
        const qty = p.qty || 1;
        const jasa = (p.jasa_nominal || p.final_jasa_nominal || 0) * qty;
        
        poProdJual += (p.total_harga_jual || p.subtotal || 0);
        poJasa += jasa;
        
        if (p.total_hpp_product !== undefined && Number(p.total_hpp_product) > 0) {
          poProdHpp += Number(p.total_hpp_product);
        } else {
          const modalPackaging = (p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0) + (p.fullset_harga_beli || 0);
          const volume = p.botol_ukuran_client || p.botol_ukuran_label || p.ukuran_botol_ml || p.volume_ml || 0;
          
          let racikan = p.racikan || [];
          if (!Array.isArray(racikan)) {
            try { racikan = JSON.parse(racikan); } catch(e) { racikan = []; }
            if (!Array.isArray(racikan)) racikan = [];
          }
          const modalRacikan = racikan.reduce((rs, r) => {
            const costPerUnit = r.hpp_bahan || r.harga_per_satuan || 0;
            const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
            return rs + volPerPcs * (costPerUnit / 1000);
          }, 0);
          
          let komponenTambahan = p.komponen_tambahan || [];
          if (!Array.isArray(komponenTambahan)) {
            try { komponenTambahan = JSON.parse(komponenTambahan); } catch(e) { komponenTambahan = []; }
            if (!Array.isArray(komponenTambahan)) komponenTambahan = [];
          }
          const modalKomponen = komponenTambahan.reduce((ks, k) => ks + (k.harga || 0), 0);
          const customHargaBeli = p.hargaBeli || p.harga_beli || 0;
          
          const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + customHargaBeli;
          poProdHpp += (hppPerPcs * qty);
        }
      });

      let legJual = 0;
      let legHpp = 0;
      rawLegalitas.forEach(l => {
        const qty = l.qty_variant || 1;
        legJual += (l.harga_jual || 0) * qty;
        legHpp += (l.harga_beli || 0) * qty;
      });

      let curProduksiExpected = 0;
      let curJasaExpected = 0;
      let curLegalitasExpected = 0;
      const shipping = po.shippingCost || 0;

      if (isCustom) {
        if (poProdHpp === 0) return; // Ignore pure-jasa custom invoices, exactly like MarginCustom.jsx
        curProduksiExpected = (poProdJual - poProdHpp);
        curJasaExpected = shipping;
      } else if (isSample) {
        curProduksiExpected = (poProdJual - poProdHpp);
        curJasaExpected = shipping;
      } else {
        curProduksiExpected = (poProdJual - poProdHpp);
        curJasaExpected = poJasa + shipping;
        curLegalitasExpected = (legJual - legHpp);
      }

      const expectedTotalMargin = curProduksiExpected + curJasaExpected + curLegalitasExpected;
      const totalHppSemua = poProdHpp + legHpp;
      const totalJualSemua = poProdJual + legJual + shipping;
      const dibayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      const status = String(po.statusBayar || 'Belum Bayar');

      let finalTotalMargin = 0;
      if (status === 'Sudah Lunas' || status === 'LUNAS' || status.toUpperCase() === 'LUNAS' || dibayar >= totalJualSemua) {
        finalTotalMargin = totalJualSemua - totalHppSemua;
      } else {
        finalTotalMargin = dibayar - totalHppSemua;
      }

      // Proporsikan margin yang didapat sesuai ekspektasi
      let curProduksi = 0;
      let curJasa = 0;
      let curLegalitas = 0;

      if (expectedTotalMargin !== 0) {
        const ratioProduksi = curProduksiExpected / expectedTotalMargin;
        const ratioJasa = curJasaExpected / expectedTotalMargin;
        const ratioLegalitas = curLegalitasExpected / expectedTotalMargin;

        curProduksi = finalTotalMargin * ratioProduksi;
        curJasa = finalTotalMargin * ratioJasa;
        curLegalitas = finalTotalMargin * ratioLegalitas;
      } else {
        // Fallback if expected margin is 0
        curProduksi = finalTotalMargin;
      }

      if (isCustom) {
        breakdownMap.Custom.produksi += curProduksi;
        breakdownMap.Custom.jasa += curJasa;
        breakdownMap.Custom.total += finalTotalMargin;
      } else if (isSample) {
        breakdownMap.Sample.produksi += curProduksi;
        breakdownMap.Sample.jasa += curJasa;
        breakdownMap.Sample.total += finalTotalMargin;
      } else {
        breakdownMap.PO.produksi += curProduksi;
        breakdownMap.PO.jasa += curJasa;
        breakdownMap.PO.legalitas += curLegalitas;
        breakdownMap.PO.total += finalTotalMargin;
      }

      marginProduksi += curProduksi;
      marginJasa += curJasa;
      marginLegalitas += curLegalitas;
      totalTransaksi++;
    });

    const breakdownArr = Object.values(breakdownMap).sort((a, b) => b.total - a.total);

    return {
      marginProduksi,
      marginJasa,
      marginLegalitas,
      totalMargin: marginProduksi + marginJasa + marginLegalitas,
      totalTransaksi,
      breakdown: breakdownArr
    };
  }, [clientPOs, dateStart, dateEnd]);

  return (
    <div className="space-y-4 md:space-y-6 max-w-7xl mx-auto animate-in fade-in zoom-in-95 duration-500">
      
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black text-[#302621] tracking-tight">
            Keseluruhan Margin
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            Laporan detail rekapitulasi margin keseluruhan dari seluruh kategori. Menampilkan {summary.totalTransaksi} transaksi.
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            className="w-full md:w-auto bg-[#8C6208] hover:bg-[#7A5507] text-white shadow-md font-bold"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* FILTER SECTION */}
      <Card className="border-[#E6D5B8]/50 shadow-sm bg-white/50 ">
        <CardContent className="p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center text-[#8C6208] font-bold text-sm mr-2">
              <Calendar className="w-4 h-4 mr-1.5" />
              PERIODE:
            </div>
            {PRESETS.map(p => (
              <button
                key={p.label}
                onClick={() => handlePreset(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-300 ${
                  activePreset === p.label && !isCustom
                    ? 'bg-[#302621] text-white shadow-md scale-105' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() => setIsCustom(true)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-300 ${
                isCustom 
                  ? 'bg-[#302621] text-white shadow-md scale-105' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Custom
            </button>
          </div>

          {isCustom && (
            <div className="flex items-center gap-2 animate-in slide-in-from-right-4 duration-300">
              <CustomDatePicker 
                value={dateStart} 
                onChange={setDateStart}
                className="w-36 text-sm h-9 border-[#E6D5B8] focus:ring-[#8C6208]"
              />
              <span className="text-slate-400 font-medium">s/d</span>
              <CustomDatePicker 
                value={dateEnd} 
                onChange={setDateEnd}
                className="w-36 text-sm h-9 border-[#E6D5B8] focus:ring-[#8C6208]"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { title: "MARGIN PRODUKSI", val: fmt(summary.marginProduksi), icon: Package },
          { title: "MARGIN JASA", val: fmt(summary.marginJasa), icon: DollarSign },
          { title: "MARGIN LEGALITAS", val: fmt(summary.marginLegalitas), icon: FileCheck },
          { title: "TOTAL MARGIN", val: fmt(summary.totalMargin), icon: FlaskConical }
        ].map((item, idx) => {
          const Icon = item.icon;
          return (
            <Card key={idx} className="border-[#E6D5B8]/50 shadow-sm bg-white hover:border-[#8C6208]/30 transition-all duration-300">
              <CardContent className="p-4 md:p-5 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute -right-4 -top-4 opacity-[0.03] pointer-events-none">
                  <Icon className="w-24 h-24" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-md bg-[#F9F5EC] text-[#8C6208]">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-wider text-slate-500">{item.title}</span>
                </div>
                <span className="text-xl md:text-2xl font-black text-[#302621] truncate">{item.val}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* MAIN CONTENT BREAKDOWN */}
      <Card className="border-[#E6D5B8]/50 shadow-sm bg-white overflow-hidden">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
          <CardTitle className="text-sm md:text-base font-black text-[#302621] uppercase tracking-wider flex items-center gap-2">
            <span className="text-[#8C6208]">$</span> KONTRIBUSI MARGIN PER KATEGORI
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {summary.breakdown.map((b, idx) => (
              <div key={b.id} className="p-6 hover:bg-slate-50 transition-colors duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F9F5EC] text-[#8C6208] flex items-center justify-center font-black text-sm">
                      #{idx + 1}
                    </div>
                    <div>
                      <h3 className="font-black text-lg text-[#302621]">{b.label}</h3>
                      <p className="text-xs font-medium text-slate-500">Peringkat kontribusi margin ke-{idx + 1}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-slate-400 mb-0.5 uppercase tracking-widest">Total Kontribusi</div>
                    <div className="text-xl font-black text-[#8C6208]">{fmt(b.total)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-white border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Dari Produksi</span>
                    <span className="font-black text-[#302621] text-sm">{fmt(b.produksi)}</span>
                  </div>
                  <div className="bg-white border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Dari Jasa</span>
                    <span className="font-black text-[#302621] text-sm">{fmt(b.jasa)}</span>
                  </div>
                  <div className="bg-white border border-slate-100 rounded-xl p-3 flex flex-col justify-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Dari Legalitas</span>
                    <span className="font-black text-[#302621] text-sm">{fmt(b.legalitas)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
